import { useState } from 'react'
import clsx from 'clsx'
import { FolderTree, Loader2 } from 'lucide-react'
import Modal from '../../../components/Modal/Modal'
import { useMdmScopePolicy, useSetMdmScopePolicy } from '../../../hooks/useMdm'
import PolicyForm from './PolicyForm'
import { errorMessage } from './mobileUtils'

const SOURCE_LABEL = {
  group: (p) => `do grupo ${p.effectiveGroupName || ''}`.trim(),
  tenant: () => 'de todos os tablets (raiz)',
  default: () => 'padrão do sistema',
}

/**
 * Política de um grupo (vale para os subgrupos) ou da raiz (`groupId` null) — CONTRACT.md §11.
 * Tablets com política própria não são afetados; o resto recebe a mudança em até um heartbeat.
 */
export default function ScopePolicyModal({ isOpen, onClose, groupId = null, groupName = null, onFeedback }) {
  const { data, isLoading, error } = useMdmScopePolicy(groupId, isOpen)
  const save = useSetMdmScopePolicy()
  const [resetMsg, setResetMsg] = useState(null)

  const hasOwn = data?.policy != null
  const scopeLabel = groupId ? `o grupo ${groupName || ''} e subgrupos` : 'todos os tablets'

  const handleSave = async (body) => {
    const res = await save.mutateAsync({ groupId, policy: body })
    const text = `Política salva para ${scopeLabel}: ${res?.changedDevices ?? 0} tablet(s) recebendo a mudança.`
    onFeedback?.({ type: 'ok', text })
    return text
  }

  const handleInherit = async () => {
    setResetMsg(null)
    try {
      const res = await save.mutateAsync({ groupId, policy: null })
      setResetMsg({ ok: true, text: `${groupId ? 'O grupo voltou a herdar' : 'A raiz voltou à política padrão'}: ${res?.changedDevices ?? 0} tablet(s) atualizados.` })
    } catch (e) {
      setResetMsg({ ok: false, text: errorMessage(e, 'Não foi possível remover a política.') })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Política — ${groupId ? groupName || 'grupo' : 'todos os tablets'}`} wide>
      {isLoading ? (
        <div className="flex items-center text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin mr-2" />Carregando política...</div>
      ) : error ? (
        <p className="text-sm text-red-600">{errorMessage(error, 'Erro ao carregar a política.')}</p>
      ) : data ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border bg-gray-50 border-gray-200 text-sm text-gray-700">
            <FolderTree className="w-4 h-4 flex-shrink-0" />
            <span>
              {hasOwn
                ? `Política própria ${groupId ? 'deste grupo' : 'da raiz'}.`
                : `Sem política própria: segue a ${SOURCE_LABEL[data.effectiveSource]?.(data) || 'padrão'}. Salvar cria uma política ${groupId ? 'para este grupo' : 'para todos'}.`}
              {' '}Vale para {data.devices} tablet(s) ({data.inheritingDevices} seguem o grupo; os outros têm política própria e não mudam).
            </span>
            {hasOwn && (
              <button
                type="button"
                onClick={handleInherit}
                disabled={save.isPending}
                className="ml-auto px-2.5 py-1 text-xs border border-gray-300 bg-white rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {groupId ? 'Voltar a herdar do grupo acima' : 'Voltar à política padrão'}
              </button>
            )}
          </div>
          {resetMsg && <p className={clsx('text-sm', resetMsg.ok ? 'text-green-700' : 'text-red-600')}>{resetMsg.text}</p>}

          <PolicyForm
            policy={data.policy ?? data.effectivePolicy}
            reloadKey={JSON.stringify(data.policy ?? data.effectivePolicy)}
            saving={save.isPending}
            onSave={handleSave}
            saveLabel={`Salvar para ${data.inheritingDevices} tablet(s)`}
            singleColumn
          />
        </div>
      ) : null}
    </Modal>
  )
}
